import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

import type { Countdown } from './use-countdown'

type SlideCountdownProps = {
  /** The time left, already read off the clock by the slide that owns it. */
  countdown: Countdown
  /** Caption above the tiles. An empty string renders no caption. */
  label?: string
}

/**
 * The glass tiles that count a dashboard slide down to its deadline.
 *
 * Days, hours and minutes — no seconds. A launch weeks out with a seconds tile
 * reads as a stopwatch, and the design these come from has three tiles.
 *
 * Presentational: the clock lives in `DashboardSlide`, which needs the same
 * reading to decide its own layout. Deciding here as well would put two clocks on
 * one slide, free to disagree for a frame.
 *
 * There is deliberately no per-digit roll animation, unlike the hardcoded slide
 * this replaces. Animation utilities in this workspace are declared per app
 * (`apps/<app>/tailwind.config.ts`), and this component renders in two of them —
 * the customer carousel and the admin preview — so a keyframe would have to be
 * copied into both configs and kept in step, which is the drift the shared
 * library exists to prevent. The tiles change once a minute, where a plain
 * re-render reads fine.
 */
export const SlideCountdown = ({ countdown, label = '' }: SlideCountdownProps) => {
  const { days, hours, minutes } = countdown

  return (
    <div className="flex shrink-0 flex-col items-center gap-2 md:gap-1">
      {label !== '' && (
        <Typography
          className="md:text-sm"
          color="text.secondary"
          variant="body1"
        >
          {label}
        </Typography>
      )}
      <div className="flex items-center gap-2 md:gap-1">
        <CountdownTile
          unit="Days"
          value={days}
        />
        <TileSeparator />
        <CountdownTile
          unit="Hours"
          value={hours}
        />
        <TileSeparator />
        <CountdownTile
          unit="Mins"
          value={minutes}
        />
      </div>
    </div>
  )
}

type CountdownTileProps = {
  value: number
  unit: string
}

/**
 * The frosted pane the digits sit on, as four stacked effects: a faint rim, a
 * dark-to-transparent tint that sinks the pane into the artwork behind it, an
 * outer drop plus a bright inner top edge for depth, and a backdrop filter whose
 * extra contrast makes the artwork's colours look trapped inside thick glass.
 *
 * An inline style rather than classes: these are translucency and blur values
 * tuned against arbitrary slide artwork, not brand colours, so the design system
 * has no token for any of them.
 */
const TILE_GLASS_STYLE = {
  border: '1px solid rgba(255, 255, 255, 0.2)',
  background: 'linear-gradient(140deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0) 100%)',
  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(35px) saturate(160%) brightness(0.85) contrast(1.1)',
  WebkitBackdropFilter: 'blur(35px) saturate(160%) brightness(0.85) contrast(1.1)',
} as const

/**
 * The digits' white-to-grey fill. Also inline, and for the same reason as the
 * glass: a text gradient is painted through `background-clip`, which no colour
 * token can express.
 */
const DIGIT_GRADIENT_STYLE = {
  backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #999999 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
} as const

const CountdownTile = ({ value, unit }: CountdownTileProps) => (
  <div
    style={TILE_GLASS_STYLE}
    className={cn(
      'relative flex h-24 w-20 flex-col items-center justify-center overflow-hidden rounded-lg',
      'md:h-16 md:w-14 md:rounded-md',
    )}
  >
    <div
      className={cn('text-3xl font-semibold leading-100 md:text-xl')}
      style={DIGIT_GRADIENT_STYLE}
    >
      {/* Padded so a single-digit hour does not shrink the row and shift its
          neighbours every time it crosses ten. */}
      {String(value).padStart(2, '0')}
    </div>
    <Typography
      className="mt-1 uppercase md:text-xs"
      color="text.secondary"
      variant="caption"
    >
      {unit}
    </Typography>
  </div>
)

const TileSeparator = () => (
  <span
    aria-hidden
    className="text-3xl font-medium leading-100 text-text-primary md:text-lg"
  >
    :
  </span>
)
